import React, { useRef, useState, useCallback } from "react";
import SupportInfo from "./components/SupportInfo";
import LicenseManager from "./components/LicenseManager";
import GeneralSettings from "./components/GeneralSettings";
import TemplateManager from "./components/TemplateManager";
import PasswordManager from "./components/PasswordManager";
import { LicenseProvider, useLicenseContext } from "./contexts/LicenseContext";
import { CryptoProvider } from "./contexts/CryptoContext";
import { ToastProvider } from "./contexts/ToastContext";
import { useI18n } from "./contexts/I18nContext";
import PdfEngineClient from "./pdfEngine/PdfEngineClient";
import { FiSettings, FiFileText, FiAward, FiHelpCircle, FiChevronRight, FiArrowLeft, FiAlertTriangle } from "react-icons/fi";
import LanguageSelector from "./components/common/LanguageSelector";

import "./utils/crypto/test-crypto.js";
import "./App.css";

function AppContent() {
  const { t } = useI18n();
  const { oid, isOfficeReady, errorMsg, retryAuth, loading, isInitialLoadSuccess, reloadLicenseStatus } = useLicenseContext();
  const [page, setPage] = useState("main");       // "main" | "settings" | section key
  const [highlightSetting, setHighlightSetting] = useState(null);
  const settingsSections = [
    { key: "general", label: t("Allgemeine Einstellungen"), icon: <FiSettings size={16} /> },
    { key: "template", label: t("E-Mail-Vorlage"), icon: <FiFileText size={16} /> },
    { key: "license", label: t("Lizenz"), icon: <FiAward size={16} /> },
    { key: "support", label: t("Hilfe & Support"), icon: <FiHelpCircle size={16} /> },
  ];

  const handleNavigate = useCallback((target, options = {}) => {
    setPage(target);
    setHighlightSetting(options.highlight ?? null);
  }, []);
  const pdfEngineRef = useRef(null);

  if (!pdfEngineRef.current) {
    const pdfEngineUrl = process.env.NODE_ENV === "development"
      ? "https://localhost:7019/"
      : `${window.location.origin}/pdf/`;
    pdfEngineRef.current = new PdfEngineClient(pdfEngineUrl);
    if (process.env.NODE_ENV === "development") {
      window.pdfEngine = pdfEngineRef.current;
    }
  }

  // OID not yet available — show loading or auth error screen
  if (!oid) {
    return (
      <div style={authScreenStyle}>
        {!isOfficeReady || loading ? (
          <p style={authTextStyle}>{t("Wird geladen...")}</p>
        ) : (
          <>
            <FiAlertTriangle size={32} color="#dc3545" style={{ marginBottom: "12px" }} />
            <p style={{ ...authTextStyle, color: "#721c24" }}>{errorMsg}</p>
            <button onClick={retryAuth} style={authRetryBtnStyle}>
              {t("Erneut versuchen")}
            </button>
          </>
        )}
      </div>
    );
  }

  // OID acquired but backend unreachable or returned error
  if (oid && !isInitialLoadSuccess && !loading) {
    return (
      <div style={authScreenStyle}>
        <FiAlertTriangle size={32} color="#dc3545" style={{ marginBottom: "12px" }} />
        <p style={{ ...authTextStyle, color: "#721c24" }}>
          {errorMsg || t("Der Server ist nicht erreichbar. Bitte überprüfen Sie Ihre Internetverbindung.")}
        </p>
        <button onClick={() => reloadLicenseStatus(oid)} style={authRetryBtnStyle}>
          {t("Erneut versuchen")}
        </button>
      </div>
    );
  }

  return (
    <CryptoProvider oid={oid}>
      <div style={{ padding: "8px 4px", maxWidth: "900px", margin: "0 auto" }}>

        {/* ── Top bar: always visible on main page ── */}
        {page === "main" && (
          <div style={topBarStyle}>
            <LanguageSelector />
            <button onClick={() => setPage("settings")} style={{ ...settingsIconBtnStyle, position: "relative" }} title={t("Einstellungen")}>
              <FiSettings size={16} color="#555" />
            </button>
          </div>
        )}

        {/* ── PasswordManager: always mounted (BroadcastChannel must stay alive) ── */}
        {/* Hidden via CSS when not on main page so it never unmounts              */}
        <div style={{ display: page === "main" ? "block" : "none" }}>
          <PasswordManager pdfEngine={pdfEngineRef.current} onNavigate={handleNavigate} />
        </div>

        {/* ── Settings list page ── */}
        {page === "settings" && (
          <div style={pageWrapStyle}>
            <div style={pageHeaderStyle}>
              <button onClick={() => setPage("main")} style={backBtnStyle} title={t("Zurück")}>
                <FiArrowLeft size={16} />
              </button>
            </div>
            <div style={pageContentStyle}>
              <div style={settingsCardStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                  <h3 style={{ margin: 0 }}>{t("Einstellungen")}</h3>
                  <LanguageSelector />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {settingsSections.map((s) => (
                    <MenuButton key={s.key} onClick={() => setPage(s.key)}>
                      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {s.icon}
                        {s.label}
                      </span>
                      <FiChevronRight size={16} style={{ color: "#999" }} />
                    </MenuButton>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Individual settings sections ── */}
        {["general", "template", "license", "support"].includes(page) && (
          <div style={pageWrapStyle}>
            <div style={pageHeaderStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
                <button onClick={() => setPage("settings")} style={backBtnStyle} title={t("Zurück")}>
                  <FiArrowLeft size={16} />
                </button>
                <LanguageSelector />
              </div>
            </div>
            <div style={pageContentStyle}>
              {page === "general"  && <GeneralSettings highlightSetting={highlightSetting} onHighlightDone={() => setHighlightSetting(null)} />}
              {page === "template" && <TemplateManager />}
              {page === "license"  && <LicenseManager />}
              {page === "support"  && <SupportInfo />}
            </div>
          </div>
        )}
      </div>
    </CryptoProvider>
  );
}

function App() {
  return (
    <LicenseProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </LicenseProvider>
  );
}

function MenuButton({ onClick, children }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ ...menuItemStyle, backgroundColor: hovered ? "#f5f5f5" : "transparent" }}
    >
      {children}
    </button>
  );
}

export default App;

// ── Styles ──────────────────────────────────────────────────

const authScreenStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100vh",
  padding: "24px",
  textAlign: "center",
};

const authTextStyle = {
  fontSize: "13px",
  color: "#555",
  marginBottom: "16px",
  lineHeight: "1.5",
};

const authRetryBtnStyle = {
  padding: "10px 24px",
  borderRadius: "6px",
  background: "#0078d7",
  color: "#fff",
  border: "none",
  cursor: "pointer",
  fontWeight: "600",
  fontSize: "14px",
};

const topBarStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "8px",
  paddingBottom: "6px",
  borderBottom: "1px solid #eee",
};

const settingsIconBtnStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  fontSize: "18px",
  padding: "2px 4px",
  lineHeight: "1",
};

const pageWrapStyle = {
  display: "flex",
  flexDirection: "column",
  height: "calc(100vh - 24px)", // subtract outer padding
};

const pageHeaderStyle = {
  flexShrink: 0,
  borderBottom: "1px solid #eee",
  paddingBottom: "6px",
  marginBottom: "12px",
  background: "#fff",
};

const pageContentStyle = {
  flex: 1,
  overflowY: "auto",
};

const backBtnStyle = {
  padding: "6px 12px",
  borderRadius: "6px",
  background: "transparent",
  color: "#0078d7",
  border: "none",
  cursor: "pointer",
  fontWeight: "500",
  fontSize: "13px",
};

const settingsCardStyle = {
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  border: "1px solid #e0e0e0",
  boxShadow: "0 4px 16px rgba(100,100,100,0.18)",
  padding: "16px 14px",
};

const menuItemStyle = {
  width: "100%",
  padding: "12px 0",
  borderRadius: "0",
  background: "transparent",
  color: "#222",
  border: "none",
  borderBottom: "1px solid #eee",
  cursor: "pointer",
  fontWeight: "400",
  fontSize: "13px",
  textAlign: "left",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};
